import React from "react";

import { DirectionsCar, Inventory2, Person } from "@mui/icons-material";
import { TFunction } from "i18next";

import {
  BulletinEntity,
  EntityType,
} from "@/components/alerts/add-v2/model/types";

/** Display order of the three types, and the icon each is known by. */
export const ENTITY_TYPE_ORDER: readonly EntityType[] = [
  "person",
  "vehicle",
  "item",
];

export const ENTITY_TYPE_ICONS: Record<EntityType, React.ReactElement> = {
  person: <Person fontSize="small" />,
  vehicle: <DirectionsCar fontSize="small" />,
  item: <Inventory2 fontSize="small" />,
};

/**
 * "Wanted #1 - John Smith" — the name a card, an editor heading and a media chip all use.
 *
 * Takes `t` rather than calling `useTranslation` so it stays a pure function: the card and the
 * editor were building two different labels for the same entity before this existed.
 *
 * Shape-for-shape the published bulletin's entity row (`alerts/entities/entityRows`): the same
 * entity must not read one way while it is being written and another once it is published. They
 * stay two functions because they read different shapes out of different i18n namespaces — so
 * they have to be changed together.
 *
 * The involvement leads, because that is the word a reader scans for: a board full of "Person #1"
 * says nothing and "Suspect #1" says what the bulletin is about. An item with no involvement has
 * nothing to lead with and is known by its own name alone — "glove", not "glove #1", because the
 * number is not part of what the officer wrote.
 */
export const entityChipLabel = (
  entity: BulletinEntity,
  index: number,
  t: TFunction,
): string => {
  const itemName = entity.type === "item" ? entity.itemName.trim() : "";
  const isNamedItem = itemName !== "" && !entity.involvement;

  let noun: string;
  if (entity.involvement === "other") {
    noun = entity.otherInvolvement.trim() || t("entities.involvement_other");
  } else if (entity.involvement) {
    noun = t(`entities.involvement_${entity.involvement}`);
  } else if (isNamedItem) {
    noun = itemName;
  } else {
    noun = t(`entities.${entity.type}`);
  }

  // What follows the number: a person's own name, and an item's own name whenever the noun above
  // is an involvement rather than the name itself.
  let trailing = "";
  if (entity.type === "person") {
    trailing = [entity.firstName, entity.lastName]
      .map((part) => part.trim())
      .filter(Boolean)
      .join(" ");
  } else if (!isNamedItem) {
    trailing = itemName;
  }

  const head = isNamedItem ? noun : `${noun} #${index}`;
  return trailing ? `${head} - ${trailing}` : head;
};
